import { IconCheck, IconCopy } from "@tabler/icons-react";
import { useState } from "react";

import { formatAiMessageForClipboard, formatAiMessageToPlainText } from "~/shared/ai/formatting";
import { ActionIcon, Tooltip } from "~/ui";

/**
 * Copies an answer as both rich text and plain text, so pasting into an email
 * keeps tables and bold while pasting into a plain field gets clean prose with
 * the markdown stripped.
 */
export const CopyAnswerButton = ({ message }: { message: string }) => {
  const [copied, setCopied] = useState(false);
  const handleCopiedUi = () => {
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const onCopy = async () => {
    try {
      const { html, text } = formatAiMessageForClipboard(message, { preserveLineBreaks: true });
      if (navigator.clipboard && "ClipboardItem" in window) {
        const blobHtml = new Blob([html], { type: "text/html" });
        const blobText = new Blob([text], { type: "text/plain" });
        const item = new ClipboardItem({ "text/html": blobHtml, "text/plain": blobText });
        await navigator.clipboard.write([item]);
        handleCopiedUi();
        return;
      }
      await navigator.clipboard.writeText(text);
      handleCopiedUi();
    } catch {
      try {
        await navigator.clipboard.writeText(formatAiMessageToPlainText(message, { preserveLineBreaks: true }));
        handleCopiedUi();
      } catch {
        // no-op: leave as not copied
      }
    }
  };

  return (
    <Tooltip label={copied ? "Copied" : "Copy"} withArrow position="top">
      <ActionIcon aria-label="Copy answer" size="sm" color={copied ? "teal" : "gray"} variant="subtle" onClick={onCopy}>
        {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
      </ActionIcon>
    </Tooltip>
  );
};
